import { useState, useEffect, useMemo } from "react";
import { useParams } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Music, Users, Calendar, PoundSterling, Plus, Search, Filter, ChevronDown, ArrowUpDown, X, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import type { Organisation, Act, ExternalEvent, Booking, ActWithParticipants, Venue } from "@shared/schema";
import { ActCard } from "@/components/acts/act-card";
import { ActFormModal } from "@/components/acts/act-form-modal";
import { ExternalEventCard } from "@/components/events/external-event-card";
import { ExternalEventFormModal } from "@/components/events/external-event-form-modal";
import { BookingFormModal } from "@/components/bookings/booking-form-modal";
import { VenueBookingModal } from "@/components/bookings/venue-booking-modal";
import { BookingTypeDialog } from "@/components/bookings/booking-type-dialog";
import { useToast } from "@/hooks/use-toast";

export default function ActManagement() {
  const { orgId } = useParams();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [eventSearchQuery, setEventSearchQuery] = useState("");
  const [bookingSearchQuery, setBookingSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("bookings");
  
  // Acts filters and sorting
  const [actFilters, setActFilters] = useState({
    genre: "all",
    actType: "all",
    hasBookings: "all",
    active: "active", // active, inactive, all
  });
  const [actSortBy, setActSortBy] = useState("name"); // name, rating, created, bookings
  
  // Events filters and sorting  
  const [eventFilters, setEventFilters] = useState({
    status: "upcoming", // all, upcoming, past, confirmed, tentative, cancelled
    venue: "all",
    hasBookings: "all",
    dateRange: "future", // all, future, past, this-month, next-month
  });
  const [eventSortBy, setEventSortBy] = useState("date-asc"); // date-asc, date-desc, name, venue
  
  // Bookings filters and sorting
  const [bookingFilters, setBookingFilters] = useState({
    status: "all", // all, pending, confirmed, completed, cancelled
    paymentStatus: "all", // all, pending, paid, partial, refunded
    dateRange: "all", // all, upcoming, past, this-month, next-month
    act: "all",
    event: "all",
  });
  const [bookingSortBy, setBookingSortBy] = useState("date-asc"); // date-asc, date-desc, fee-asc, fee-desc, commission-asc, commission-desc, status

  // Check permissions
  useEffect(() => {
    if (!orgId) {
      setLocation("/");
      return;
    }

    const organisationId = parseInt(orgId || "0");
    const hasAccess = user?.role === 'god' || 
      user?.organisationUsers?.some(ou => 
        ou.organisation.id === organisationId && 
        (ou.role === 'owner' || ou.role === 'operator')
      );

    if (!hasAccess) {
      setLocation("/");
    }
  }, [orgId, user]); // Removed setLocation from dependencies

  // Fetch organisation details
  const { data: organisation, isLoading: isLoadingOrg } = useQuery<Organisation>({
    queryKey: [`/api/organisations/${orgId}`],
    queryFn: async () => {
      const response = await fetch(`/api/organisations`);
      if (!response.ok) throw new Error("Failed to fetch organisation");
      const orgs = await response.json();
      return orgs.find((org: Organisation) => org.id === parseInt(orgId || "0"));
    },
    enabled: !!orgId,
  });

  // Check if artist management is enabled
  useEffect(() => {
    if (organisation && !organisation.artistManagement) {
      setLocation("/");
    }
  }, [organisation]); // Removed setLocation from dependencies

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showActModal, setShowActModal] = useState(false);
  const [editingAct, setEditingAct] = useState<ActWithParticipants | undefined>();
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<(ExternalEvent & { venue?: Venue }) | undefined>();
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showVenueBookingModal, setShowVenueBookingModal] = useState(false);
  const [showBookingTypeDialog, setShowBookingTypeDialog] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | undefined>();
  const [bookingEventId, setBookingEventId] = useState<number | undefined>();
  const [bookingActId, setBookingActId] = useState<number | undefined>();

  // Fetch acts
  const { data: acts = [], isLoading: isLoadingActs } = useQuery<ActWithParticipants[]>({
    queryKey: [`/api/acts?organisationId=${orgId}`],
    queryFn: async () => {
      const response = await fetch(`/api/acts?organisationId=${orgId}`);
      if (!response.ok) throw new Error("Failed to fetch acts");
      return response.json();
    },
    enabled: !!orgId && !!organisation?.artistManagement,
  });

  // Fetch external events
  const { data: externalEvents = [], isLoading: isLoadingEvents } = useQuery<(ExternalEvent & { venue?: Venue })[]>({
    queryKey: [`/api/external-events?organisationId=${orgId}`],
    queryFn: async () => {
      const response = await fetch(`/api/external-events?organisationId=${orgId}`);
      if (!response.ok) throw new Error("Failed to fetch external events");
      return response.json();
    },
    enabled: !!orgId && !!organisation?.artistManagement,
  });

  // Fetch bookings
  const { data: bookings = [], isLoading: isLoadingBookings } = useQuery<Booking[]>({
    queryKey: [`/api/bookings?organisationId=${orgId}`],
    queryFn: async () => {
      const response = await fetch(`/api/bookings?organisationId=${orgId}`);
      if (!response.ok) throw new Error("Failed to fetch bookings");
      return response.json();
    },
    enabled: !!orgId && !!organisation?.artistManagement,
  });

  // Get unique genres from acts
  const availableGenres = useMemo(() => {
    const genres = new Set<string>();
    acts.forEach(act => {
      act.genre?.forEach(g => genres.add(g));
    });
    return Array.from(genres).sort();
  }, [acts]);

  // Get unique venues from events
  const availableVenues = useMemo(() => {
    const venues = new Map<number, string>();
    externalEvents.forEach(event => {
      if (event.venue) {
        venues.set(event.venue.id, event.venue.name);
      }
    });
    return Array.from(venues.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [externalEvents]);

  // Filter and sort acts
  const filteredAndSortedActs = useMemo(() => {
    let filtered = acts.filter(act => {
      // Search filter
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        const matchesSearch = 
          act.name?.toLowerCase().includes(search) || 
          act.participants?.some(p => p.participant.name.toLowerCase().includes(search));
        if (!matchesSearch) return false;
      }
      
      // Genre filter
      if (actFilters.genre !== "all" && !act.genre?.includes(actFilters.genre)) {
        return false;
      }
      
      // Act type filter
      if (actFilters.actType !== "all" && act.actType !== actFilters.actType) {
        return false;
      }
      
      // Has bookings filter
      if (actFilters.hasBookings !== "all") {
        const hasBookings = bookings.some(b => b.actId === act.id);
        if (actFilters.hasBookings === "yes" && !hasBookings) return false;
        if (actFilters.hasBookings === "no" && hasBookings) return false;
      }
      
      // Active filter
      if (actFilters.active !== "all") {
        if (actFilters.active === "active" && act.active === false) return false;
        if (actFilters.active === "inactive" && act.active !== false) return false;
      }
      
      return true;
    });
    
    // Sort
    filtered.sort((a, b) => {
      switch (actSortBy) {
        case "name":
          const nameA = a.name || a.participants?.[0]?.participant.name || "";
          const nameB = b.name || b.participants?.[0]?.participant.name || "";
          return nameA.localeCompare(nameB);
        case "rating":
          return (b.rating || 0) - (a.rating || 0);
        case "created":
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case "bookings":
          const bookingsA = bookings.filter(b => b.actId === a.id).length;
          const bookingsB = bookings.filter(b => b.actId === b.id).length;
          return bookingsB - bookingsA;
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [acts, searchQuery, actFilters, actSortBy, bookings]);

  // Filter and sort events
  const filteredAndSortedEvents = useMemo(() => {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nextMonthEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    
    let filtered = externalEvents.filter(event => {
      const eventDate = new Date(event.date);
      
      // Search filter
      if (eventSearchQuery) {
        const search = eventSearchQuery.toLowerCase();
        const matchesSearch = 
          event.name.toLowerCase().includes(search) ||
          event.venue?.name?.toLowerCase().includes(search) ||
          event.notes?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (eventFilters.status !== "all") {
        if (eventFilters.status === "upcoming" && eventDate <= now) return false;
        if (eventFilters.status === "past" && eventDate > now) return false;
        if (eventFilters.status === "confirmed" && event.status !== "confirmed") return false;
        if (eventFilters.status === "tentative" && event.status !== "tentative") return false;
        if (eventFilters.status === "cancelled" && event.status !== "cancelled") return false;
      }
      
      // Venue filter
      if (eventFilters.venue !== "all" && event.venue?.id !== parseInt(eventFilters.venue)) {
        return false;
      }
      
      // Has bookings filter
      if (eventFilters.hasBookings !== "all") {
        const hasBookings = bookings.some(b => b.externalEventId === event.id);
        if (eventFilters.hasBookings === "yes" && !hasBookings) return false;
        if (eventFilters.hasBookings === "no" && hasBookings) return false;
      }
      
      // Date range filter
      switch (eventFilters.dateRange) {
        case "future":
          if (eventDate <= now) return false;
          break;
        case "past":
          if (eventDate > now) return false;
          break;
        case "this-month":
          if (eventDate < thisMonth || eventDate >= nextMonth) return false;
          break;
        case "next-month":
          if (eventDate < nextMonth || eventDate > nextMonthEnd) return false;
          break;
      }
      
      return true;
    });
    
    // Sort
    filtered.sort((a, b) => {
      switch (eventSortBy) {
        case "date-asc":
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        case "date-desc":
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        case "name":
          return a.name.localeCompare(b.name);
        case "venue":
          const venueA = a.venue?.name || "";
          const venueB = b.venue?.name || "";
          return venueA.localeCompare(venueB);
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [externalEvents, eventSearchQuery, eventFilters, eventSortBy, bookings]);

  // Filter and sort bookings
  const filteredAndSortedBookings = useMemo(() => {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nextMonthEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    
    let filtered = bookings.filter(booking => {
      // Search filter
      if (bookingSearchQuery) {
        const search = bookingSearchQuery.toLowerCase();
        // Find the act and event names for searching
        const act = acts.find(a => a.id === booking.actId);
        const event = externalEvents.find(e => e.id === booking.externalEventId);
        const actName = act?.name || act?.participants?.[0]?.participant.name || "";
        const eventName = event?.name || "";
        
        const matchesSearch = 
          actName.toLowerCase().includes(search) ||
          eventName.toLowerCase().includes(search) ||
          booking.notes?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (bookingFilters.status !== "all" && booking.status !== bookingFilters.status) {
        return false;
      }
      
      // Payment status filter
      if (bookingFilters.paymentStatus !== "all" && booking.paymentStatus !== bookingFilters.paymentStatus) {
        return false;
      }
      
      // Act filter
      if (bookingFilters.act !== "all" && booking.actId !== parseInt(bookingFilters.act)) {
        return false;
      }
      
      // Event filter
      if (bookingFilters.event !== "all" && booking.externalEventId !== parseInt(bookingFilters.event)) {
        return false;
      }
      
      // Date range filter
      if (booking.performanceTime && bookingFilters.dateRange !== "all") {
        const performanceDate = new Date(booking.performanceTime);
        switch (bookingFilters.dateRange) {
          case "upcoming":
            if (performanceDate <= now) return false;
            break;
          case "past":
            if (performanceDate > now) return false;
            break;
          case "this-month":
            if (performanceDate < thisMonth || performanceDate >= nextMonth) return false;
            break;
          case "next-month":
            if (performanceDate < nextMonth || performanceDate > nextMonthEnd) return false;
            break;
        }
      }
      
      return true;
    });
    
    // Sort
    filtered.sort((a, b) => {
      switch (bookingSortBy) {
        case "date-asc":
          if (!a.performanceTime && !b.performanceTime) return 0;
          if (!a.performanceTime) return 1;
          if (!b.performanceTime) return -1;
          return new Date(a.performanceTime).getTime() - new Date(b.performanceTime).getTime();
        case "date-desc":
          if (!a.performanceTime && !b.performanceTime) return 0;
          if (!a.performanceTime) return -1;
          if (!b.performanceTime) return 1;
          return new Date(b.performanceTime).getTime() - new Date(a.performanceTime).getTime();
        case "fee-asc":
          return (a.fee || 0) - (b.fee || 0);
        case "fee-desc":
          return (b.fee || 0) - (a.fee || 0);
        case "commission-asc":
          return (a.commissionAmount || 0) - (b.commissionAmount || 0);
        case "commission-desc":
          return (b.commissionAmount || 0) - (a.commissionAmount || 0);
        case "status":
          return (a.status || "").localeCompare(b.status || "");
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [bookings, bookingSearchQuery, bookingFilters, bookingSortBy, acts, externalEvents]);

  // Delete act mutation
  const deleteActMutation = useMutation({
    mutationFn: async (actId: number) => {
      const response = await fetch(`/api/acts/${actId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error("Failed to delete act");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/acts`] });
      toast({
        title: "Act Removed",
        description: "The act has been removed from your roster.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const stats = {
    totalActs: acts.length,
    activeBookings: bookings.filter(b => b.status === 'confirmed').length,
    upcomingEvents: externalEvents.filter(e => new Date(e.date) > new Date()).length,
    totalRevenue: bookings
      .filter(b => b.status === 'completed' && b.fee)
      .reduce((sum, b) => sum + (b.fee || 0), 0) / 100,
  };

  if (isLoadingOrg) {
    return (
      <PageBackground>
        <AppHeader />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <Music className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700" />
            <p className="text-slate-700">Loading act management...</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header with New Booking and Calendar Buttons */}
        <div className="mb-6 flex flex-col sm:flex-row gap-2">
          <Button 
            className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
            onClick={() => {
              setBookingEventId(undefined);
              setBookingActId(undefined);
              setEditingBooking(undefined);
              setShowBookingTypeDialog(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Booking
          </Button>
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
            onClick={() => setLocation(`/organisations/${orgId}/bookings/calendar`)}
          >
            <CalendarDays className="h-4 w-4 mr-2" />
            Calendar
          </Button>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3 bg-white/30 backdrop-blur-xl">
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="acts">Acts</TabsTrigger>
          </TabsList>

          {/* Acts Tab */}
          <TabsContent value="acts" className="space-y-4">
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardHeader>
                <div className="space-y-4">
                  {/* Search and Add Act */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search acts..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-full"
                      />
                    </div>
                    <Button 
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => {
                        setEditingAct(undefined);
                        setShowActModal(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Act
                    </Button>
                  </div>
                  
                  {/* Filters */}
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                    {/* Genre Filter */}
                    {availableGenres.length > 0 && (
                      <Select value={actFilters.genre} onValueChange={(value) => setActFilters(prev => ({ ...prev, genre: value }))}>
                        <SelectTrigger className="w-full sm:w-[140px]">
                          <SelectValue placeholder="Genre" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Genres</SelectItem>
                          {availableGenres.map(genre => (
                            <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {/* Act Type Filter */}
                    <Select value={actFilters.actType} onValueChange={(value) => setActFilters(prev => ({ ...prev, actType: value }))}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="duo">Duo</SelectItem>
                        <SelectItem value="band">Band</SelectItem>
                        <SelectItem value="ensemble">Ensemble</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Has Bookings Filter */}
                    <Select value={actFilters.hasBookings} onValueChange={(value) => setActFilters(prev => ({ ...prev, hasBookings: value }))}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <SelectValue placeholder="Bookings" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Acts</SelectItem>
                        <SelectItem value="yes">Has Bookings</SelectItem>
                        <SelectItem value="no">No Bookings</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Active Filter */}
                    <Select value={actFilters.active} onValueChange={(value) => setActFilters(prev => ({ ...prev, active: value }))}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active Only</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="all">All Status</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Sort By */}
                    <Select value={actSortBy} onValueChange={setActSortBy}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <ArrowUpDown className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Sort" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="name">Name</SelectItem>
                        <SelectItem value="rating">Rating</SelectItem>
                        <SelectItem value="created">Newest</SelectItem>
                        <SelectItem value="bookings">Most Booked</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Clear Filters Button */}
                    {(searchQuery || actFilters.genre !== "all" || actFilters.actType !== "all" || actFilters.hasBookings !== "all" || actFilters.active !== "active") && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSearchQuery("");
                          setActFilters({
                            genre: "all",
                            actType: "all",
                            hasBookings: "all",
                            active: "active"
                          });
                          setActSortBy("name");
                        }}
                        className="w-full sm:w-auto"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Clear Filters
                      </Button>
                    )}
                  </div>
                  
                  {/* Results count */}
                  {searchQuery || actFilters.genre !== "all" || actFilters.actType !== "all" || actFilters.hasBookings !== "all" || actFilters.active !== "active" ? (
                    <div className="text-sm text-slate-600">
                      Showing {filteredAndSortedActs.length} of {acts.length} acts
                    </div>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent>
                {acts.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      No acts yet
                    </h3>
                    <p className="text-slate-600 mb-4">
                      Start building your act roster by adding your first act.
                    </p>
                    <Button 
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => {
                        setEditingAct(undefined);
                        setShowActModal(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First Act
                    </Button>
                  </div>
                ) : filteredAndSortedActs.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      No matching acts
                    </h3>
                    <p className="text-slate-600">
                      Try adjusting your filters or search query
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredAndSortedActs.map((act) => (
                      <ActCard
                        key={act.id}
                        act={act}
                        organisationId={parseInt(orgId || "0")}
                        onEdit={() => {
                          setEditingAct(act);
                          setShowActModal(true);
                        }}
                        onDelete={() => {
                          const displayName = act.name || act.participants?.[0]?.participant.name || 'this act';
                          if (confirm(`Are you sure you want to remove ${displayName}?`)) {
                            deleteActMutation.mutate(act.id);
                          }
                        }}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* External Events Tab */}
          <TabsContent value="events" className="space-y-4">
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardHeader>
                <div className="space-y-4">
                  {/* Search bar and Add Event Button */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                      <Input
                        placeholder="Search events..."
                        value={eventSearchQuery}
                        onChange={(e) => setEventSearchQuery(e.target.value)}
                        className="pl-9 w-full"
                      />
                    </div>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => {
                        setEditingEvent(undefined);
                        setShowEventModal(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Event
                    </Button>
                  </div>
                  
                  {/* Filters */}
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                    {/* Status Filter */}
                    <Select value={eventFilters.status} onValueChange={(value) => setEventFilters(prev => ({ ...prev, status: value }))}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upcoming">Upcoming</SelectItem>
                        <SelectItem value="all">All Events</SelectItem>
                        <SelectItem value="past">Past</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="tentative">Tentative</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Date Range Filter */}
                    <Select value={eventFilters.dateRange} onValueChange={(value) => setEventFilters(prev => ({ ...prev, dateRange: value }))}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <SelectValue placeholder="Date Range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="future">Future</SelectItem>
                        <SelectItem value="all">All Dates</SelectItem>
                        <SelectItem value="past">Past</SelectItem>
                        <SelectItem value="this-month">This Month</SelectItem>
                        <SelectItem value="next-month">Next Month</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Venue Filter */}
                    {availableVenues.length > 0 && (
                      <Select value={eventFilters.venue} onValueChange={(value) => setEventFilters(prev => ({ ...prev, venue: value }))}>
                        <SelectTrigger className="w-full sm:w-[140px]">
                          <SelectValue placeholder="Venue" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Venues</SelectItem>
                          {availableVenues.map(([id, name]) => (
                            <SelectItem key={id} value={id.toString()}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {/* Has Bookings Filter */}
                    <Select value={eventFilters.hasBookings} onValueChange={(value) => setEventFilters(prev => ({ ...prev, hasBookings: value }))}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <SelectValue placeholder="Bookings" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Events</SelectItem>
                        <SelectItem value="yes">Has Bookings</SelectItem>
                        <SelectItem value="no">No Bookings</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Sort By */}
                    <Select value={eventSortBy} onValueChange={setEventSortBy}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <ArrowUpDown className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Sort" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date-asc">Date (Earliest)</SelectItem>
                        <SelectItem value="date-desc">Date (Latest)</SelectItem>
                        <SelectItem value="name">Name</SelectItem>
                        <SelectItem value="venue">Venue</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Clear Filters Button */}
                    {(eventSearchQuery || eventFilters.status !== "upcoming" || eventFilters.dateRange !== "future" || eventFilters.venue !== "all" || eventFilters.hasBookings !== "all") && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEventSearchQuery("");
                          setEventFilters({
                            status: "upcoming",
                            dateRange: "future",
                            venue: "all",
                            hasBookings: "all"
                          });
                          setEventSortBy("date-asc");
                        }}
                        className="w-full sm:w-auto"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Clear Filters
                      </Button>
                    )}
                  </div>
                  
                  {/* Results count */}
                  {(eventSearchQuery || eventFilters.status !== "upcoming" || eventFilters.dateRange !== "future" || eventFilters.venue !== "all" || eventFilters.hasBookings !== "all") ? (
                    <div className="text-sm text-slate-600">
                      Showing {filteredAndSortedEvents.length} of {externalEvents.length} events
                    </div>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent>
                {externalEvents.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      No external events
                    </h3>
                    <p className="text-slate-600 mb-4">
                      Add external events to book your artists for performances.
                    </p>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => {
                        setEditingEvent(undefined);
                        setShowEventModal(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add External Event
                    </Button>
                  </div>
                ) : filteredAndSortedEvents.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      No matching events
                    </h3>
                    <p className="text-slate-600">
                      Try adjusting your filters
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredAndSortedEvents.map((event) => (
                      <ExternalEventCard
                        key={event.id}
                        event={event}
                        organisationId={parseInt(orgId || "0")}
                        onEdit={() => {
                          setEditingEvent(event);
                          setShowEventModal(true);
                        }}
                        onDelete={() => {
                          if (confirm(`Are you sure you want to delete ${event.name}?`)) {
                            // TODO: Add delete mutation
                          }
                        }}
                        onCreateBooking={() => {
                          setBookingEventId(event.id);
                          setBookingActId(undefined);
                          setEditingBooking(undefined);
                          setShowBookingModal(true);
                        }}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardHeader>
                <div className="space-y-4">
                  {/* Search bar */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input
                      placeholder="Search bookings..."
                      value={bookingSearchQuery}
                      onChange={(e) => setBookingSearchQuery(e.target.value)}
                      className="pl-9 w-full"
                    />
                  </div>
                  
                  {/* Filters */}
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                    {/* Status Filter */}
                    <Select value={bookingFilters.status} onValueChange={(value) => setBookingFilters(prev => ({ ...prev, status: value }))}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Payment Status Filter */}
                    <Select value={bookingFilters.paymentStatus} onValueChange={(value) => setBookingFilters(prev => ({ ...prev, paymentStatus: value }))}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <SelectValue placeholder="Payment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Payments</SelectItem>
                        <SelectItem value="pending">Payment Pending</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                        <SelectItem value="refunded">Refunded</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Date Range Filter */}
                    <Select value={bookingFilters.dateRange} onValueChange={(value) => setBookingFilters(prev => ({ ...prev, dateRange: value }))}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <SelectValue placeholder="Date Range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Dates</SelectItem>
                        <SelectItem value="upcoming">Upcoming</SelectItem>
                        <SelectItem value="past">Past</SelectItem>
                        <SelectItem value="this-month">This Month</SelectItem>
                        <SelectItem value="next-month">Next Month</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Act Filter */}
                    {acts.length > 0 && (
                      <Select value={bookingFilters.act} onValueChange={(value) => setBookingFilters(prev => ({ ...prev, act: value }))}>
                        <SelectTrigger className="w-full sm:w-[140px]">
                          <SelectValue placeholder="Act" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Acts</SelectItem>
                          {acts.map((act) => (
                            <SelectItem key={act.id} value={act.id.toString()}>
                              {act.name || act.participants?.[0]?.participant.name || "Unnamed Act"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {/* Event Filter */}
                    {externalEvents.length > 0 && (
                      <Select value={bookingFilters.event} onValueChange={(value) => setBookingFilters(prev => ({ ...prev, event: value }))}>
                        <SelectTrigger className="w-full sm:w-[140px]">
                          <SelectValue placeholder="Event" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Events</SelectItem>
                          {externalEvents.map((event) => (
                            <SelectItem key={event.id} value={event.id.toString()}>{event.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {/* Sort By */}
                    <Select value={bookingSortBy} onValueChange={setBookingSortBy}>
                      <SelectTrigger className="w-full sm:w-[140px]">
                        <ArrowUpDown className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Sort" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date-desc">Date (Latest)</SelectItem>
                        <SelectItem value="date-asc">Date (Earliest)</SelectItem>
                        <SelectItem value="fee-desc">Fee (Highest)</SelectItem>
                        <SelectItem value="fee-asc">Fee (Lowest)</SelectItem>
                        <SelectItem value="commission-desc">Commission (Highest)</SelectItem>
                        <SelectItem value="commission-asc">Commission (Lowest)</SelectItem>
                        <SelectItem value="status">Status</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Clear Filters Button */}
                    {(bookingSearchQuery || bookingFilters.status !== "all" || bookingFilters.paymentStatus !== "all" || bookingFilters.dateRange !== "all" || bookingFilters.act !== "all" || bookingFilters.event !== "all") && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setBookingSearchQuery("");
                          setBookingFilters({
                            status: "all",
                            paymentStatus: "all",
                            dateRange: "all",
                            act: "all",
                            event: "all"
                          });
                          setBookingSortBy("date-asc");
                        }}
                        className="w-full sm:w-auto col-span-2 sm:col-span-1"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Clear Filters
                      </Button>
                    )}
                  </div>
                  
                  {/* Results count */}
                  {(bookingSearchQuery || bookingFilters.status !== "all" || bookingFilters.paymentStatus !== "all" || bookingFilters.dateRange !== "all" || bookingFilters.act !== "all" || bookingFilters.event !== "all") && (
                    <div className="text-sm text-slate-600">
                      Showing {filteredAndSortedBookings.length} of {bookings.length} bookings
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <div className="text-center py-12">
                    <PoundSterling className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      No bookings yet
                    </h3>
                    <p className="text-slate-600">
                      Create your first booking to start managing artist performances.
                    </p>
                  </div>
                ) : filteredAndSortedBookings.length === 0 ? (
                  <div className="text-center py-12">
                    <PoundSterling className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      No matching bookings
                    </h3>
                    <p className="text-slate-600">
                      Try adjusting your filters
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Group bookings by date */}
                    {(() => {
                      // Group bookings by date
                      const bookingsByDate = filteredAndSortedBookings.reduce((acc: Record<string, any[]>, booking: any) => {
                        const date = booking.performanceTime || booking.externalEvent?.date;
                        if (!date) return acc;
                        
                        const dateKey = new Date(date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        });
                        
                        if (!acc[dateKey]) {
                          acc[dateKey] = [];
                        }
                        acc[dateKey].push(booking);
                        return acc;
                      }, {});
                      
                      // Sort dates
                      const sortedDates = Object.keys(bookingsByDate).sort((a, b) => {
                        const dateA = new Date(bookingsByDate[a][0].performanceTime || bookingsByDate[a][0].externalEvent?.date);
                        const dateB = new Date(bookingsByDate[b][0].performanceTime || bookingsByDate[b][0].externalEvent?.date);
                        return bookingSortBy.includes('desc') ? dateB.getTime() - dateA.getTime() : dateA.getTime() - dateB.getTime();
                      });
                      
                      return sortedDates.map(dateKey => (
                        <div key={dateKey} className="space-y-3">
                          {/* Date Header */}
                          <div className="flex items-center gap-3">
                            <CalendarDays className="h-5 w-5 text-purple-600" />
                            <h3 className="text-lg font-semibold text-slate-800">
                              {dateKey}
                            </h3>
                            <div className="flex-1 h-px bg-gradient-to-r from-purple-200 via-purple-100 to-transparent" />
                            <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                              {bookingsByDate[dateKey].length} {bookingsByDate[dateKey].length === 1 ? 'booking' : 'bookings'}
                            </Badge>
                          </div>
                          
                          {/* Bookings for this date */}
                          <div className="space-y-3">
                            {bookingsByDate[dateKey].map((booking: any) => (
                              <div 
                                key={booking.id} 
                                className="p-4 bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer group relative overflow-hidden"
                                style={{
                                  background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                                  boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
                                }}
                                onClick={() => setLocation(`/organisations/${orgId}/bookings/${booking.id}`)}
                              >
                                {/* Specular highlight on hover */}
                                <div 
                                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                                  style={{
                                    background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
                                  }}
                                />
                                <div className="flex items-center justify-between relative z-10">
                                  <div>
                                    <p className="font-medium">
                                      {booking.act?.name || booking.act?.participants?.[0]?.participant?.name || 'Unknown Act'}
                                    </p>
                                    <p className="text-sm text-slate-600">
                                      {booking.externalEvent?.name} - {booking.externalEvent?.venue?.name || 'Unknown Venue'}
                                    </p>
                                    {booking.performanceTime && (
                                      <p className="text-sm text-slate-500">
                                        {new Date(booking.performanceTime).toLocaleTimeString('en-US', {
                                          hour: 'numeric',
                                          minute: '2-digit',
                                          hour12: true
                                        })}
                                        {booking.durationMinutes && ` (${booking.durationMinutes} min)`}
                                      </p>
                                    )}
                                  </div>
                          <div className="text-right">
                            <div className="flex items-center gap-2 justify-end mb-1">
                              <Badge>{booking.status}</Badge>
                              {booking.paymentStatus && booking.paymentStatus !== 'pending' && (
                                <Badge variant={booking.paymentStatus === 'paid' ? 'default' : 'secondary'}>
                                  {booking.paymentStatus}
                                </Badge>
                              )}
                            </div>
                            {booking.fee && (
                              <div className="space-y-1">
                                <p className="text-sm font-medium">
                                  Fee: £{(booking.fee / 100).toFixed(2)}
                                </p>
                                {booking.commissionAmount && (
                                  <p className="text-xs text-slate-600">
                                    Commission: {booking.commissionType === 'percentage' && booking.commissionValue
                                      ? `${(booking.commissionValue / 100).toFixed(1)}%`
                                      : 'Flat'
                                    }
                                    {' '}
                                    (£{(booking.commissionAmount / 100).toFixed(2)})
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Act Form Modal */}
        <ActFormModal
          open={showActModal}
          onOpenChange={setShowActModal}
          organisationId={parseInt(orgId || "0")}
          act={editingAct}
        />

        {/* External Event Form Modal */}
        <ExternalEventFormModal
          open={showEventModal}
          onOpenChange={setShowEventModal}
          organisationId={parseInt(orgId || "0")}
          event={editingEvent}
        />

        {/* Booking Type Selection Dialog */}
        <BookingTypeDialog
          open={showBookingTypeDialog}
          onOpenChange={setShowBookingTypeDialog}
          onSelectType={(type) => {
            if (type === "event") {
              setShowBookingModal(true);
            } else {
              setShowVenueBookingModal(true);
            }
          }}
        />

        {/* Event Booking Form Modal */}
        <BookingFormModal
          open={showBookingModal}
          onOpenChange={setShowBookingModal}
          organisationId={parseInt(orgId || "0")}
          booking={editingBooking}
          eventId={bookingEventId}
          actId={bookingActId}
        />

        {/* Venue Booking Form Modal */}
        <VenueBookingModal
          open={showVenueBookingModal}
          onOpenChange={setShowVenueBookingModal}
          organisationId={parseInt(orgId || "0")}
          actId={bookingActId}
        />
      </main>
    </PageBackground>
  );
}